import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { erroDaApi } from '@/lib/erroDaApi';
import { useAuthStore, type AuthUser } from '@/stores/authStore';

const campo =
  'w-full border border-border rounded-md px-sm py-sm text-[14px] text-ink bg-surface focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent';

/**
 * Primeiro acesso com a senha provisória que o administrador passou: antes de
 * qualquer tela, a pessoa define a própria senha (U-01).
 */
export default function TrocarSenhaPage() {
  const navigate = useNavigate();
  const user = useAuthStore((estado) => estado.user);
  const atualizarUsuario = useAuthStore((estado) => estado.atualizarUsuario);
  const clearAuth = useAuthStore((estado) => estado.clearAuth);
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  const salvar = useMutation({
    mutationFn: async () =>
      (await api.put<{ data: AuthUser }>('/perfil/senha', { nova, nova_confirmation: confirmacao })).data.data,
    onSuccess: (atualizado) => {
      atualizarUsuario(atualizado);
      navigate('/', { replace: true });
    },
  });

  const sair = async () => {
    // Revoga o token provisório: sem isso ele continua valendo por até 8h e
    // dá para definir a senha sem a atual mesmo depois de "sair".
    try {
      await api.post('/logout');
    } catch {
      // Ignorado: mesmo sem o backend confirmar, o token local é descartado.
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  // Quem já tem senha definida não deveria abrir esta tela — receberia 422
  // "Informe a senha atual." ao tentar salvar. Vai para a página inicial.
  if (!user?.trocar_senha) {
    return <Navigate to={user ? '/' : '/login'} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dominant px-md">
      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          salvar.mutate();
        }}
        className="w-full max-w-[400px] bg-surface border border-border rounded-xl shadow-sm p-lg"
      >
        <KeyRound size={28} className="text-accent" />
        <h1 className="mt-sm text-[20px] font-bold text-ink">Olá, {user?.nome.split(' ')[0]}</h1>
        <p className="mt-xs text-[14px] text-muted">
          Você entrou com uma senha provisória. Defina a sua senha para continuar — ela precisa ter pelo menos 8
          caracteres.
        </p>

        <label className="mt-lg block">
          <span className="mb-xs block text-[12px] font-bold text-ink-soft">Nova senha</span>
          <input type="password" autoComplete="new-password" value={nova} onChange={(e) => setNova(e.target.value)} required minLength={8} className={campo} />
        </label>
        <label className="mt-md block">
          <span className="mb-xs block text-[12px] font-bold text-ink-soft">Confirme a nova senha</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            required
            minLength={8}
            className={campo}
          />
        </label>

        {salvar.isError && <p className="mt-md text-[14px] text-danger">{erroDaApi(salvar.error, 'Não foi possível salvar a senha.')}</p>}

        <button
          type="submit"
          disabled={salvar.isPending}
          className="mt-lg w-full inline-flex items-center justify-center gap-xs bg-accent text-white font-bold text-[14px] rounded-md py-sm disabled:opacity-60"
        >
          {salvar.isPending ? <Loader2 className="animate-spin" size={16} /> : 'Definir senha e entrar'}
        </button>
        <button type="button" onClick={sair} className="mt-sm w-full text-[13px] text-muted hover:text-ink">
          Sair
        </button>
      </form>
    </div>
  );
}
